import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const APP_LOGO = "https://customer-assets-rejwkqb3.emergentagent.net/job_edu-censo-admin/artifacts/8c23fucn_Logo%20Censo911.png";

const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

const GRADO_ORDINAL = { 1: "Primer", 2: "Segundo", 3: "Tercer", 4: "Cuarto", 5: "Quinto", 6: "Sexto" };

const CAMPOS = [
  { key: "lenguajes" }, { key: "saberes" }, { key: "etica" }, { key: "humano" },
];
const NEM_TO_NUM = { NAE: 5, AE: 7, AS: 8.5, ADS: 10 };

// "8.3" → "ocho punto tres"
const NUM_LETRAS = ["cero", "uno", "dos", "tres", "cuatro", "cinco", "seis", "siete", "ocho", "nueve", "diez"];
const digitoALetra = (d) => NUM_LETRAS[parseInt(d, 10)] ?? d;
const numeroEnLetras = (num) => {
  if (num == null) return "";
  const [ent, dec = "0"] = num.toFixed(1).split(".");
  const e = parseInt(ent, 10);
  const entPalabra = e === 10 ? "diez" : NUM_LETRAS[e] || String(e);
  return `${entPalabra} punto ${digitoALetra(dec)}`;
};

const fechaEnLetras = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T12:00:00");
  if (isNaN(d.getTime())) return iso;
  return `${d.getDate()} días del mes de ${MESES[d.getMonth()]} de ${d.getFullYear()}`;
};

const fechaCortaLetras = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${d.getDate()} de ${MESES[d.getMonth()]} de ${d.getFullYear()}`;
};

const upper = (s) => (s || "").toUpperCase();
const nombreCompleto = (a) => [a.nombre, a.apellido_paterno, a.apellido_materno].filter(Boolean).join(" ");

const toNum = (raw, escala) => {
  if (raw == null || raw === "") return null;
  if (escala === "NUMERICA") { const n = parseFloat(raw); return isNaN(n) ? null : n; }
  return NEM_TO_NUM[raw] ?? null;
};
const promedioFinal = (tris, escala) => {
  const vals = [];
  for (const t of [1, 2, 3]) {
    const tri = tris?.[t];
    if (!tri) continue;
    for (const c of CAMPOS) {
      const n = toNum(tri[c.key], escala);
      if (n !== null) vals.push(n);
    }
  }
  if (!vals.length) return null;
  return vals.reduce((s, v) => s + v, 0) / vals.length;
};

export default function ConstanciaPrint() {
  const [sp] = useSearchParams();
  const plantelId = sp.get("plantel_id");
  const alcance = sp.get("alcance") || "INDIVIDUAL";
  const tipoConst = (sp.get("tipo") || "ESTANDAR").toUpperCase();
  const grupoId = sp.get("grupo_id");
  const alumnoId = sp.get("alumno_id");
  const ciclo = sp.get("ciclo") || "";
  const lugar = sp.get("lugar") || "Ciudad y Puerto de Tampico, Tamaulipas";
  const fecha = sp.get("fecha") || new Date().toISOString().slice(0, 10);

  const [plantel, setPlantel] = useState(null);
  const [director, setDirector] = useState(null);
  const [grupo, setGrupo] = useState(null);
  const [alumnos, setAlumnos] = useState([]);
  const [triMap, setTriMap] = useState({});
  const [loading, setLoading] = useState(true);

  const needCalifs = tipoConst === "PROMEDIO" || tipoConst === "BECA";

  useEffect(() => {
    (async () => {
      try {
        const [{ data: pls }, staffResp] = await Promise.all([
          api.get("/planteles"),
          api.get(`/planteles/${plantelId}/staff-list`).catch(() => ({ data: {} })),
        ]);
        const p = pls.find((x) => x.id === plantelId);
        setPlantel(p || null);
        setDirector(staffResp.data?.director || null);

        if (grupoId) {
          const g = (await api.get(`/grupos/${grupoId}`)).data;
          setGrupo(g);
          // Para BAJA incluimos bajas también
          const params = { grupo_id: grupoId };
          if (tipoConst === "BAJA") params.include_bajas = true;
          const { data: als } = await api.get("/alumnos", { params });
          const list = (alcance === "INDIVIDUAL" && alumnoId) ? als.filter((a) => a.id === alumnoId) : als;
          setAlumnos(list);

          if (needCalifs && list.length) {
            const results = await Promise.all(
              list.map((a) =>
                api.get(`/calificaciones/alumno/${a.id}`, { params: { ciclo: g.ciclo } })
                  .then((r) => [a.id, r.data.trimestres])
                  .catch(() => [a.id, { 1: null, 2: null, 3: null }])
              )
            );
            const m = {};
            results.forEach(([aid, tris]) => { m[aid] = tris; });
            setTriMap(m);
          }
        }
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [plantelId, grupoId, alumnoId, alcance, needCalifs, tipoConst]);

  const directorNombreLargo = useMemo(() => {
    if (!director) return "____________________________________________";
    const raw = [director.nombre, director.apellido_paterno, director.apellido_materno].filter(Boolean).join(" ");
    // Formato "Nombre Apellidos" en title case (como aparece en el template)
    return raw
      .toLowerCase()
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }, [director]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#6B7280]">Cargando…</div>;
  if (!alumnos.length) return <div className="min-h-screen flex items-center justify-center text-[#9F2241]">Sin alumnos para constancia</div>;

  const escala = plantel?.escala_boletas === "NUMERICA" ? "NUMERICA" : "LETRAS";

  return (
    <div className="min-h-screen bg-[#EAECEE] print:bg-white p-6 print:p-0">
      <div className="mb-4 flex justify-between items-center print:hidden">
        <div className="text-sm text-[#374151]">
          {alumnos.length} constancia{alumnos.length === 1 ? "" : "s"} · {plantel?.nombre} ·{" "}
          {tipoConst === "ESTANDAR" ? "Simple" : tipoConst === "PROMEDIO" ? "Con promedio" : tipoConst === "BAJA" ? "De baja" : "Trámite de beca"}
        </div>
        <button onClick={() => window.print()} className="px-3 py-1.5 rounded bg-[#235B4E] text-white text-sm">Imprimir</button>
      </div>

      {alumnos.map((a) => (
        <Hoja
          key={a.id}
          alumno={a}
          plantel={plantel}
          grupo={grupo}
          ciclo={ciclo}
          lugar={lugar}
          fecha={fecha}
          directorNombreLargo={directorNombreLargo}
          tipoConst={tipoConst}
          tris={triMap[a.id]}
          escala={escala}
        />
      ))}

      <style>{`
        @page { size: Letter portrait; margin: 18mm 22mm; }
        .const-page {
          width: 172mm;
          height: 243mm;
          box-sizing: border-box;
          page-break-after: always;
          display: flex;
          flex-direction: column;
        }
        .const-page:last-child { page-break-after: auto; }
        @media print {
          html, body { margin: 0 !important; padding: 0 !important; background: white; }
          .const-page { box-shadow: none !important; margin: 0 !important; }
        }
      `}</style>
    </div>
  );
}

function Hoja({ alumno, plantel, grupo, ciclo, lugar, fecha, directorNombreLargo, tipoConst, tris, escala }) {
  const promFinal = tris ? promedioFinal(tris, escala) : null;
  const promStr = promFinal != null ? promFinal.toFixed(1) : null;
  const promLetras = promFinal != null ? numeroEnLetras(promFinal) : null;

  const gradoLabel = grupo ? `${GRADO_ORDINAL[grupo.grado] || `${grupo.grado}°`} año grupo "${grupo.letra}"` : "grado correspondiente";
  const fechaBajaLetras = fechaCortaLetras(alumno.fecha_baja);

  return (
    <div className="const-page bg-white mx-auto mb-6 print:mb-0 px-8 py-6 print:p-0" style={{ fontFamily: "'Times New Roman', Georgia, serif", color: "#111827" }}>
      {/* Membrete */}
      <div className="flex items-center gap-4 pb-3">
        {plantel?.logo_url ? (
          <img src={plantel.logo_url} alt="Logo" className="w-16 h-16 object-contain" crossOrigin="anonymous" />
        ) : (
          <img src={APP_LOGO} alt="Censo 911" className="w-16 h-16 object-contain" crossOrigin="anonymous" />
        )}
        <div className="flex-1 text-center">
          <div className="text-[14pt] font-bold uppercase leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>{plantel?.nombre || "—"}</div>
          <div className="text-[9pt] text-[#374151]">
            CCT: <span className="font-mono">{plantel?.cct || "—"}</span>
            {plantel?.turno ? ` · Turno ${plantel.turno}` : ""}
          </div>
          <div className="text-[8.5pt] text-[#374151]">
            {[plantel?.domicilio, plantel?.municipio, plantel?.entidad]
              .filter(Boolean)
              .map((s) => s.toLowerCase().replace(/\b(\p{L})(\p{L}*)/gu, (_, a, b) => a.toUpperCase() + b))
              .join(", ")}
          </div>
        </div>
        <div className="w-16 h-16" />
      </div>

      {/* Salutación */}
      <div className="mt-8 text-[12pt] leading-[1.7]">
        <p className="font-bold text-[13pt]">A QUIEN CORRESPONDA</p>
        <p className="font-bold text-[13pt] mb-6">PRESENTE</p>

        {/* Cuerpo por tipo */}
        {tipoConst === "BAJA" ? (
          <p className="text-justify indent-8 mb-6">
            El suscrito <strong>Profr. {directorNombreLargo}</strong>, Director de la escuela{" "}
            <strong>{plantel?.nombre || "—"}</strong>, turno {plantel?.turno ? plantel.turno.charAt(0).toUpperCase() + plantel.turno.slice(1).toLowerCase() : "—"} con C.C.T.{" "}
            <strong className="font-mono">{plantel?.cct || "—"}</strong>, hace constar que el (la) alumno(a):{" "}
            <strong>{nombreCompleto(alumno)}</strong>, CURP <strong className="font-mono">{alumno.curp || "—"}</strong>,
            del <strong>{gradoLabel}</strong> causa baja en la escuela a partir del{" "}
            <strong>{fechaBajaLetras || fechaCortaLetras(fecha)}</strong>.
          </p>
        ) : (
          <p className="text-justify indent-8 mb-6">
            El suscrito <strong>Profr. {directorNombreLargo}</strong>, Director de la escuela{" "}
            <strong>{plantel?.nombre || "—"}</strong>, turno {plantel?.turno ? plantel.turno.charAt(0).toUpperCase() + plantel.turno.slice(1).toLowerCase() : "—"} con C.C.T.{" "}
            <strong className="font-mono">{plantel?.cct || "—"}</strong>, hace constar que el (la) alumno(a):{" "}
            <strong>{nombreCompleto(alumno)}</strong>, CURP <strong className="font-mono">{alumno.curp || "—"}</strong>,
            del <strong>{gradoLabel}</strong> se encuentra inscrito de forma regular el ciclo escolar{" "}
            <strong>{ciclo || "—"}</strong> conforme a los planes y programas que establecen la Secretaría de Educación Pública
            {(tipoConst === "PROMEDIO" || tipoConst === "BECA") && promStr && (
              <>
                , con un promedio general hasta la fecha de <strong>{promStr}</strong> ({promLetras})
              </>
            )}
            .
          </p>
        )}

        {/* Cláusula de propósito */}
        <p className="text-justify indent-8 mb-8">
          Se extiende la presente constancia de estudios a petición de la parte interesada para{" "}
          {tipoConst === "BECA" ? <>realizar <strong>trámite de Beca</strong></> : "los fines legales y/o administrativos que al interesado convengan"},
          en la <strong>{lugar}</strong> a los <strong>{fechaEnLetras(fecha)}</strong>.
        </p>

        {/* Cierre */}
        <div className="text-center mt-8">
          <div className="tracking-[0.35em] font-bold text-[12pt]">A T E N T A M E N T E</div>
          <div className="mt-1 font-semibold text-[11pt]">DIRECTOR</div>
          <div className="font-semibold text-[11pt]">DE LA ESCUELA</div>
          <div className="mt-16">
            <div className="mx-auto w-80 border-t border-black" />
            <div className="text-[11.5pt] font-semibold mt-1">Profr. {directorNombreLargo}</div>
          </div>
        </div>
      </div>

      {/* Pie — línea reservada (sin folio ni sistema) */}
      <div className="mt-auto pt-3 border-t border-[#E5E7EB]" style={{ minHeight: "10mm" }} />
    </div>
  );
}
